import axios from "axios";
import { getApiBase } from "../apiBase";
import { buildPluginsHeader } from "./registry";
import {
  CSV_IMPORT_BOOL_FIELDS,
  CSV_IMPORT_FILE_FIELDS,
  CSV_IMPORT_MATERIALS_JSON_FIELDS,
  CSV_IMPORT_MULTIFILE_FIELDS,
  CSV_IMPORT_MULTI_FIELDS,
  CSV_IMPORT_NUM_FIELDS,
  CSV_IMPORT_PERMITS_JSON_FIELDS,
  CSV_IMPORT_SERVICE_SPEED_FIELDS,
  invertMapping,
} from "./projectCsvImportFields";
import {
  isHttpUrl,
  normalizeFileImportUrl,
  parseFileUrlsFromCsvCell,
} from "./projectCsvImportFiles";
import {
  buildCommercialAcfGroupsPatch,
  mergeCommercialDomainsPartial,
  mergeCommercialIntoAcfGroups,
  projectScalarsFromCommercialDomains,
  readCommercialDomains,
  readCommercialDomainsForRow,
  syncServiceSpeedAcrossCommercialDomains,
} from "./commercialProjectAcf";
import { COMMERCIAL_DOMAIN_LIST, emptyCommercialAcfGroups, normalizeMulti, serviceSpeedSlug } from "./commercialProjectSchema";
import {
  collectDepartmentsFromCsvRow,
  DEPARTMENTS_FIELD_ID,
  departmentsMappingSkipped,
} from "./projectCsvImportDepartments";
import { coerceMaterialsJsonImportValue, MATERIALS_JSON_FIELD_ID, applyMaterialTotalsToFirstPermit } from "./materialsJsonImport";
import { coercePermitsJsonImportValue, PERMITS_JSON_FIELD_ID } from "./permitsJsonImport";

const AUTH_TOKEN_KEY = "projectBoardAuthToken";
const DEFAULT_MATCH_FIELD = "project_summary.work_order";

/** Flat commercial field id → projects table scalar column (partial CSV PATCH). */
const FLAT_FIELD_TO_SCALAR = {
  "project_summary.work_order": "work_order",
  "project_summary.customer": "customer",
  "project_summary.project": "name",
  "project_summary.type": "project_type",
  "project_summary.subtype": "subtype",
  "project_summary.status": "status",
  "project_summary.phase": "phase_label",
  "project_summary.foc_date": "foc_date",
  "project_summary.sales_rep": "sales_rep",
  "project_summary.site_walk": "site_walk",
  "project_summary.service_location": "service_location",
  "project_summary.a_location": "a_location",
  "project_summary.z_location": "z_location",
  "project_summary.new_customer": "new_customer",
  "accounting_details.mrc": "mrc",
  "accounting_details.nrc": "non_recurring_charge",
  "accounting_details.term": "term_in_months",
  "accounting_details.invoiced": "invoiced_date",
};

function authHeaders() {
  const h = { ...buildPluginsHeader() };
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) h.Authorization = `Bearer ${token}`;
  } catch {
    /* ignore */
  }
  return h;
}

function toBool(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v;
  const s = String(v).trim().toLowerCase();
  if (["1", "true", "yes", "y"].includes(s)) return true;
  if (["0", "false", "no", "n"].includes(s)) return false;
  return null;
}

function toNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : null;
}

function coerceFieldValue(fieldId, raw) {
  if (raw === null || raw === undefined) return undefined;
  const s = typeof raw === "string" ? raw.trim() : raw;
  if (s === "") return undefined;
  if (CSV_IMPORT_BOOL_FIELDS.has(fieldId)) {
    const b = toBool(s);
    return b === null ? undefined : b;
  }
  if (CSV_IMPORT_NUM_FIELDS.has(fieldId)) {
    const n = toNum(s);
    return n === null ? undefined : n;
  }
  if (CSV_IMPORT_MULTI_FIELDS.has(fieldId)) {
    const arr = normalizeMulti(s);
    return arr.length ? arr : undefined;
  }
  if (CSV_IMPORT_FILE_FIELDS.has(fieldId)) {
    const url = normalizeFileImportUrl(s);
    return isHttpUrl(url) ? url : undefined;
  }
  if (CSV_IMPORT_MULTIFILE_FIELDS.has(fieldId)) {
    const urls = parseFileUrlsFromCsvCell(s);
    return urls.length ? urls : undefined;
  }
  if (CSV_IMPORT_PERMITS_JSON_FIELDS.has(fieldId)) {
    return coercePermitsJsonImportValue(fieldId, s);
  }
  if (CSV_IMPORT_MATERIALS_JSON_FIELDS.has(fieldId)) {
    return coerceMaterialsJsonImportValue(fieldId, s);
  }
  if (CSV_IMPORT_SERVICE_SPEED_FIELDS.has(fieldId)) {
    const slug = serviceSpeedSlug(s);
    return slug || undefined;
  }
  return String(s);
}

function parseFieldId(fieldId) {
  const dot = fieldId.indexOf(".");
  if (dot < 0) return null;
  const domain = fieldId.slice(0, dot);
  const key = fieldId.slice(dot + 1);
  if (!COMMERCIAL_DOMAIN_LIST.includes(domain) || !key) return null;
  return { domain, key };
}

/**
 * @param {Record<string, string>} csvRow
 * @param {Record<string, string>} destToColumn commercial field id -> csv header
 * @param {Record<string, string>} [columnToDest] csv header -> field id (multi-column merge)
 */
export function mappedRowToPayload(csvRow, destToColumn, columnToDest) {
  const payload = { _source: "import_csv" };
  const ctd = columnToDest || {};
  const departmentsMapped = Object.values(ctd).includes(DEPARTMENTS_FIELD_ID);
  for (const [fieldId, csvCol] of Object.entries(destToColumn)) {
    if (!fieldId || !csvCol) continue;
    if (fieldId === DEPARTMENTS_FIELD_ID) {
      const deps = collectDepartmentsFromCsvRow(csvRow, ctd, destToColumn);
      if (deps.length) payload[fieldId] = deps;
      continue;
    }
    const val = coerceFieldValue(fieldId, csvRow[csvCol]);
    if (val !== undefined) payload[fieldId] = val;
  }
  // Heuristic harvest only when departments were not mapped and not explicitly "Do not import".
  if (!payload[DEPARTMENTS_FIELD_ID] && !departmentsMapped && !departmentsMappingSkipped(ctd)) {
    const deps = collectDepartmentsFromCsvRow(csvRow, ctd, destToColumn);
    if (deps.length) payload[DEPARTMENTS_FIELD_ID] = deps;
  }
  return payload;
}

/** Only PATCH scalar columns for fields present in this CSV row (never null-out unmapped). */
function scalarsForCsvImport(domains, flatPayload) {
  const raw = projectScalarsFromCommercialDomains(domains);
  const allow = new Set();
  for (const fieldId of Object.keys(flatPayload || {})) {
    const scalar = FLAT_FIELD_TO_SCALAR[fieldId];
    if (scalar) allow.add(scalar);
  }
  const scalars = {};
  for (const [k, v] of Object.entries(raw)) {
    if (!allow.has(k)) continue;
    if (v === null || v === undefined) continue;
    if (typeof v === "number" && !Number.isFinite(v)) continue;
    scalars[k] = v;
  }
  return scalars;
}

function existingDomainsForImport(match) {
  if (!match || typeof match !== "object") return emptyCommercialAcfGroups();
  if (match.acf_groups_json !== undefined || match.acf_domains !== undefined) {
    return readCommercialDomainsForRow(match, match.acf_domains);
  }
  return readCommercialDomains(match);
}

function flatPayloadToCommercialBody(flatPayload, match) {
  const imported = emptyCommercialAcfGroups();
  let materialTotals = undefined;
  for (const [fieldId, val] of Object.entries(flatPayload)) {
    if (fieldId === "_source") continue;
    if (fieldId === MATERIALS_JSON_FIELD_ID) {
      materialTotals = val;
      continue;
    }
    const parsed = parseFieldId(fieldId);
    if (!parsed) continue;
    imported[parsed.domain][parsed.key] = val;
  }
  const updating = match != null && typeof match === "object" && match.id != null;
  const existingAcf =
    match?.acf_groups_json && typeof match.acf_groups_json === "object" ? match.acf_groups_json : {};
  let domains = syncServiceSpeedAcrossCommercialDomains(
    updating
      ? mergeCommercialDomainsPartial(existingDomainsForImport(match), imported)
      : imported
  );
  if (materialTotals !== undefined) {
    domains = applyMaterialTotalsToFirstPermit(domains, materialTotals);
  }
  const acf_groups_json = updating
    ? mergeCommercialIntoAcfGroups(existingAcf, domains)
    : buildCommercialAcfGroupsPatch(domains);
  const scalars = scalarsForCsvImport(domains, flatPayload);
  return {
    _source: flatPayload._source || "import_csv",
    acf_groups_json,
    ...scalars,
    cvin_expansion: false,
  };
}

function workOrderFromRow(row) {
  const wo =
    row?.work_order ??
    row?.acf_groups_json?.project_summary?.work_order ??
    "";
  return String(wo).trim().toLowerCase();
}

function workOrderFromPayload(payload) {
  const wo = payload[DEFAULT_MATCH_FIELD] ?? payload.work_order ?? "";
  return String(wo).trim().toLowerCase();
}

function payloadMatchKey(payload, matchField) {
  const val = payload[matchField];
  if (val == null || String(val).trim() === "") return "";
  return String(val).trim().toLowerCase();
}

function toIndexMap(rows, keyFn) {
  const m = new Map();
  for (const r of rows) {
    const k = keyFn(r);
    if (!k) continue;
    m.set(k, r);
  }
  return m;
}

function registerProjectInIndexes(indexes, row) {
  if (!row || row.id == null) return;
  const woKey = workOrderFromRow(row);
  if (woKey) indexes.byWo.set(woKey, row);
}

function findMatch(indexes, payload, matchField) {
  const key = payloadMatchKey(payload, matchField);
  if (matchField === DEFAULT_MATCH_FIELD && key) {
    return indexes.byWo.get(key) || null;
  }
  if (key && indexes.byWo.has(key)) return indexes.byWo.get(key);
  const woKey = workOrderFromPayload(payload);
  if (woKey) return indexes.byWo.get(woKey) || null;
  return null;
}

function bodyWithoutConflictingWorkOrder(body, indexes, projectId) {
  const wo = body.work_order ?? body.acf_groups_json?.project_summary?.work_order;
  if (wo == null || wo === "") return body;
  const woKey = String(wo).trim().toLowerCase();
  const occupant = indexes.byWo.get(woKey);
  if (occupant && occupant.id !== projectId) {
    const next = { ...body };
    delete next.work_order;
    if (next.acf_groups_json?.project_summary) {
      next.acf_groups_json = {
        ...next.acf_groups_json,
        project_summary: { ...next.acf_groups_json.project_summary },
      };
      delete next.acf_groups_json.project_summary.work_order;
    }
    return next;
  }
  return body;
}

async function patchProject(base, projectId, body, headers, indexes) {
  const safe = bodyWithoutConflictingWorkOrder(body, indexes, projectId);
  const res = await axios.patch(`${base}/projects/${projectId}`, safe, { headers });
  return res.data;
}

async function createThenPatch(base, body, headers, indexes) {
  const createBody = {
    work_order: body.work_order,
    name: body.name,
    customer: body.customer,
    status: body.status,
    _source: body._source,
  };
  if (createBody.work_order) {
    const woKey = String(createBody.work_order).trim().toLowerCase();
    if (indexes.byWo.has(woKey)) delete createBody.work_order;
  }
  const c = await axios.post(`${base}/projects`, createBody, { headers });
  const row = c.data || {};
  if (row?.id == null) return row;
  return (await patchProject(base, row.id, body, headers, indexes)) || row;
}

/**
 * @param {Record<string, string>[]} csvRows
 * @param {Record<string, string>} columnToDest csv header -> commercial field id (required for department merge)
 * @param {object} options
 * @param {string} [options.matchField] e.g. project_summary.work_order
 * @param {boolean} options.createIfMissing
 * @param {function} [options.onProgress]
 */
export async function importProjectsFromCsv(csvRows, columnToDest, options = {}) {
  const matchField = options.matchField || DEFAULT_MATCH_FIELD;
  const createIfMissing = options.createIfMissing !== false;
  const onProgress = options.onProgress;
  const destToColumn = invertMapping(columnToDest);

  const base = getApiBase();
  const headers = authHeaders();
  const existingRes = await axios.get(`${base}/projects`, { headers });
  const existing = Array.isArray(existingRes.data) ? existingRes.data : [];
  const indexes = {
    byWo: toIndexMap(existing, workOrderFromRow),
  };

  let created = 0;
  let updated = 0;
  let skipped = 0;
  const errors = [];

  for (let i = 0; i < csvRows.length; i += 1) {
    const flatPayload = mappedRowToPayload(csvRows[i], destToColumn, columnToDest);
    const permitsCol = destToColumn[PERMITS_JSON_FIELD_ID];
    const permitsRaw = permitsCol ? csvRows[i][permitsCol] : "";
    if (
      permitsCol &&
      permitsRaw != null &&
      String(permitsRaw).trim() !== "" &&
      flatPayload[PERMITS_JSON_FIELD_ID] === undefined
    ) {
      errors.push(
        `Row ${i + 2}: could not parse permits JSON in column "${permitsCol}" (check empty "" fields and keep JSON on one line or fully quoted in CSV)`
      );
    }
    const materialsCol = destToColumn[MATERIALS_JSON_FIELD_ID];
    const materialsRaw = materialsCol ? csvRows[i][materialsCol] : "";
    if (
      materialsCol &&
      materialsRaw != null &&
      String(materialsRaw).trim() !== "" &&
      flatPayload[MATERIALS_JSON_FIELD_ID] === undefined
    ) {
      errors.push(
        `Row ${i + 2}: could not parse material totals JSON in column "${materialsCol}" (check empty "" fields and keep JSON on one line or fully quoted in CSV)`
      );
    }
    const match = findMatch(indexes, flatPayload, matchField);
    const body = flatPayloadToCommercialBody(flatPayload, match);

    const hasIdentity = Boolean(workOrderFromPayload(flatPayload));

    try {
      let row = null;
      if (match?.id != null) {
        row = await patchProject(base, match.id, body, headers, indexes);
        registerProjectInIndexes(indexes, row);
        updated += 1;
      } else if (createIfMissing && hasIdentity) {
        row = await createThenPatch(base, body, headers, indexes);
        registerProjectInIndexes(indexes, row);
        created += 1;
      } else {
        skipped += 1;
      }
    } catch (e) {
      const apiErr = e?.response?.data?.error;
      errors.push(
        typeof apiErr === "string"
          ? apiErr
          : e?.message || `Row ${i + 2}: import failed`
      );
    }

    if (typeof onProgress === "function") {
      onProgress({
        current: i + 1,
        total: csvRows.length,
        created,
        updated,
        skipped,
        errors,
      });
    }
  }

  return { total: csvRows.length, created, updated, skipped, errors };
}
